import { ApiError, buildApiUrl, request, requestBlob } from './api';
import type { DicomClassification } from '../types/dicom';

export interface AtlasCatalogItem {
  id: string;
  name: string;
  space: string;
  description: string;
  template_file_name: string;
  template_size_bytes: number;
  parcellation_file_name: string | null;
  parcellation_size_bytes: number;
  label_count: number;
  study_instance_uid: string;
  series_instance_uid: string;
  viewer_config_url: string;
  family?: string;
  contrast?: string | null;
  classification?: DicomClassification;
  resolution_mm?: number | null;
  dimensions?: [number, number, number] | null;
  content_version?: string | null;
  data_kind?: 'anatomical' | 'labelmap' | 'probability';
}

interface AtlasCatalogResponse {
  atlases: AtlasCatalogItem[];
}

export interface ParcelizationVolume {
  hemisphere: 'left' | 'right';
  nifti_url: string;
  cache_key: string;
  content_version: string;
}

export interface ParcelizationCatalogItem {
  id: string;
  label: string;
  color: [number, number, number];
  source_atlas: string;
  volumes: ParcelizationVolume[];
}

interface ParcelizationCatalogResponse {
  parcelizations: ParcelizationCatalogItem[];
}

export async function getAtlasCatalog(token: string): Promise<AtlasCatalogItem[]> {
  const response = await request<AtlasCatalogResponse>('/atlases', { token });
  if (!response || !Array.isArray(response.atlases)) {
    throw new ApiError('Atlas catalog could not be loaded.', 500);
  }
  return response.atlases;
}

export async function getParcelizationCatalog(
  token: string
): Promise<ParcelizationCatalogItem[]> {
  const response = await request<ParcelizationCatalogResponse>('/atlases/parcelizations', {
    token,
  });
  if (!response || !Array.isArray(response.parcelizations)) {
    throw new ApiError('LeadDBS parcelizations could not be loaded.', 500);
  }
  if (response.parcelizations.length === 0) {
    throw new ApiError('No LeadDBS parcelization volumes are installed.', 404);
  }
  return response.parcelizations.map(item => ({
    ...item,
    volumes: item.volumes.map(volume => ({
      ...volume,
      nifti_url: buildApiUrl(volume.nifti_url.replace(/^\/api\/v1/, '')),
    })),
  }));
}

export function getAtlasThumbnail(atlas: AtlasCatalogItem, token: string, signal?: AbortSignal) {
  const version = atlas.content_version ? `?v=${encodeURIComponent(atlas.content_version)}` : '';
  return requestBlob(`/atlases/${encodeURIComponent(atlas.id)}/thumbnail${version}`, {
    token,
    signal,
  });
}
